/**
 * billingService.js — Billing & Subscription Service Layer
 * ─────────────────────────────────────────────────────────
 * Created: 2026-03-04
 * Purpose: Frontend service that wraps the 3 browser-facing billing Edge
 *          Functions (billing-checkout, billing-portal, billing-entitlements).
 *
 * Architecture:
 *   - Uses supabase.functions.invoke() which automatically attaches
 *     the user's JWT (Authorization header) and handles CORS.
 *   - The x-active-team-id header is injected via getTeamHeaders() so
 *     Edge Functions can scope operations to the correct team.
 *   - All three Edge Functions are deployed and ACTIVE on Supabase.
 *
 * Edge Function contracts:
 *   GET  billing-entitlements → { team_id, plan, usage, billing }
 *   POST billing-checkout     → { checkout_url } or { checkout_url, existing_subscription }
 *   POST billing-portal       → { portal_url }
 *
 * Change log:
 *   2026-03-04  Initial creation — getEntitlements, createCheckout, openPortal,
 *               getPlans (static). Matches Edge Functions from commit d611ed5.
 */

import { supabase } from '../lib/supabase';

// ── Helpers ─────────────────────────────────────────────────

/**
 * Build the x-active-team-id header required by all billing Edge Functions.
 * The Edge Functions use this to scope DB queries via RLS and validate
 * team membership before proceeding.
 */
function getTeamHeaders() {
  const teamId = supabase.rest.headers['x-active-team-id'];
  if (!teamId) {
    throw new Error('No active team selected — cannot call billing API');
  }
  return { 'x-active-team-id': teamId };
}

// ── Static plan data ────────────────────────────────────────

/**
 * Static plan definitions matching the seed data in
 * supabase/migrations/20260304200000_subscription_billing.sql
 *
 * These are used by the PricingPage so it can render instantly
 * without a network call. The Edge Function returns the *actual*
 * plan for the team; this is just for display.
 *
 * The `features` arrays power the bullet-point lists on plan cards.
 */
const PLANS = [
  {
    id: 'workbench',
    display_name: 'Workbench',
    monthly_price_cents: 900,
    included_variants: 50,
    overage_rate_cents: 4,
    features: [
      'All features included',
      'Unlimited users (reasonable use applies)',
    ],
  },
  {
    id: 'dreamer',
    display_name: 'Dreamer',
    monthly_price_cents: 2900,
    included_variants: 2500,
    overage_rate_cents: 4,
    features: [
      'All features included',
      'Unlimited users (reasonable use applies)',
    ],
  },
  {
    id: 'maker',
    display_name: 'Maker',
    monthly_price_cents: 3900,
    included_variants: 5000,
    overage_rate_cents: 4,
    popular: true, // UI badge: "Most Popular"
    features: [
      'All features included',
      'Unlimited users (reasonable use applies)',
    ],
  },
  {
    id: 'studio',
    display_name: 'Studio',
    monthly_price_cents: 8900,
    included_variants: 20000,
    overage_rate_cents: 4,
    features: [
      'All features included',
      'Unlimited users (reasonable use applies)',
    ],
  },
];

// ── API calls ───────────────────────────────────────────────

/**
 * Fetch the current team's billing entitlements from the
 * billing-entitlements Edge Function.
 *
 * Returns a structured object:
 *   { team_id, plan: {...}, usage: {...}, billing: {...} }
 *
 * If the team has no billing_accounts row (new team, hasn't
 * subscribed yet), the Edge Function returns a "No Plan" fallback
 * with live variant counts — see billing-entitlements/index.ts lines 79-100.
 */
export async function getEntitlements() {
  const { data, error } = await supabase.functions.invoke('billing-entitlements', {
    method: 'GET',
    headers: getTeamHeaders(),
  });

  if (error) throw new Error(error.message || 'Failed to fetch entitlements');
  return data;
}

/**
 * Create a Stripe Checkout Session for the given plan.
 *
 * @param {string} planId — one of 'workbench', 'dreamer', 'maker', 'studio'
 * @returns {{ checkout_url: string, existing_subscription?: boolean }}
 *
 * If the team already has an active Stripe subscription, the Edge Function
 * returns a portal URL instead (checkout_url will be the portal URL and
 * existing_subscription=true). The caller should handle both cases.
 *
 * The checkout session uses success_url = /?checkout=success
 * and cancel_url = /?checkout=canceled (set in the Edge Function).
 */
export async function createCheckout(planId) {
  let timeoutId;
  const invokePromise = supabase.functions.invoke('billing-checkout', {
    method: 'POST',
    headers: getTeamHeaders(),
    body: { plan_id: planId },
  });

  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error('Checkout timed out. Please try again.'));
    }, 45000);
  });

  let data;
  let error;
  try {
    ({ data, error } = await Promise.race([invokePromise, timeoutPromise]));
  } finally {
    clearTimeout(timeoutId);
  }

  if (error) {
    let edgeMessage = '';
    try {
      if (typeof error.context?.json === 'function') {
        const body = await error.context.json();
        if (typeof body?.error === 'string') {
          edgeMessage = body.error;
        }
      }
    } catch {
      // Keep fallback error when response body is unavailable.
    }

    throw new Error(edgeMessage || error.message || 'Failed to create checkout session');
  }
  if (!data?.checkout_url) {
    throw new Error('Checkout did not return a redirect URL. Please try again.');
  }
  return data;
}

/**
 * Get a Stripe Billing Portal URL for the current team.
 *
 * The portal lets users manage payment methods, view invoices,
 * and cancel/change subscriptions — all hosted by Stripe.
 *
 * @returns {{ portal_url: string }}
 * @throws if team has no billing account (hasn't subscribed yet)
 */
export async function openPortal() {
  const { data, error } = await supabase.functions.invoke('billing-portal', {
    method: 'POST',
    headers: getTeamHeaders(),
  });

  if (error) throw new Error(error.message || 'Failed to open billing portal');
  return data;
}

/**
 * Update team overage policy.
 * overage_enabled=true  -> bill overages monthly
 * overage_enabled=false -> pause variants over cap
 */
export async function updateOveragePolicy(overageEnabled) {
  const { data, error } = await supabase.functions.invoke('billing-settings-update', {
    method: 'POST',
    headers: getTeamHeaders(),
    body: { overage_enabled: Boolean(overageEnabled) },
  });

  if (error) throw new Error(error.message || 'Failed to update billing settings');
  if (data?.error) throw new Error(data.error);
  return data?.settings;
}

/**
 * Return static plan definitions for the pricing page.
 * No network call needed — these match the DB seed data.
 */
export function getPlans() {
  return PLANS;
}

/**
 * Format cents as a dollar string.
 * @param {number} cents — e.g. 2900
 * @returns {string} — e.g. "$29"
 */
export function formatPrice(cents) {
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}
